import { useMemo, useRef, useState, useEffect } from "react";
import { useGlobalContext } from "../../data/GlobalContext";

function ClockIcon() {
	return (
		<svg className="lb-clock" viewBox="0 0 24 24" aria-hidden="true">
			<circle
				cx="12"
				cy="12"
				r="9"
				fill="none"
				stroke="currentColor"
				strokeWidth="2.2"
			/>

			<path
				d="M12 7v6h5"
				fill="none"
				stroke="currentColor"
				strokeWidth="2.2"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	);
}

function RankBadge({ rank, isYou }) {
	if (isYou) {
		return (
			<div className="lb-rank-star">
				<img
					src="/images/game/jili-rush/JILI-RUSH-STAR.webp"
					alt={`Rank ${rank}`}
				/>

				<span className="lb-star-rank">{rank}</span>
			</div>
		);
	}

	if (rank <= 3) {
		return (
			<div className="lb-medal" aria-label={`Rank ${rank}`}>
				<img src={`/images/game/jili-rush/JILI_RUSH-TOP${rank}.webp`} alt="" />
			</div>
		);
	}

	return <div className="lb-rank-number">{rank}</div>;
}

export default function Leaderboard({
	updateText = "Leaderboard updates every 10 minutes",
}) {
	const scrollerRef = useRef(null);

	const [scrollRatio, setScrollRatio] = useState(0);
	const {
		jiliFortuneRushLeaderboard: entries,
		jiliFortuneRushLeaderboardLoading: loading,
		jiliFortuneRushLeaderboardError: error,
	} = useGlobalContext();

	const sortedEntries = useMemo(() => {
		return [...entries].sort((a, b) => Number(a.rank) - Number(b.rank));
	}, [entries]);

	const updateThumb = () => {
		const el = scrollerRef.current;

		if (!el) return;

		const max = el.scrollHeight - el.clientHeight;

		setScrollRatio(max > 0 ? el.scrollTop / max : 0);
	};

	const scrollByRows = (direction) => {
		const el = scrollerRef.current;

		if (!el) return;

		el.scrollBy({
			top: direction * 150,
			behavior: "smooth",
		});
	};

	return (
		<section className="leaderboard-card" aria-label="Leaderboard">
			<div className="leaderboard-inner">
				<header className="leaderboard-title-row">
					<h2>LEADERBOARD</h2>
				</header>

				<div className="leaderboard-panel">
					<div className="leaderboard-list-shell">
						<div
							ref={scrollerRef}
							className="leaderboard-list"
							onScroll={updateThumb}
						>
							<div className="leaderboard-row leaderboard-header">
								<div>Rank</div>
								<div>Username</div>
								<div>Turnover</div>
							</div>

							{loading && entries.length === 0 && (
								<div className="leaderboard-empty">Loading leaderboard...</div>
							)}

							{!loading && error && (
								<div className="leaderboard-empty">{error}</div>
							)}

							{!loading && !error && entries.length === 0 && (
								<div className="leaderboard-empty">
									No leaderboard data available.
								</div>
							)}

							{!error &&
								entries.map((item) => (
									<div
										className={`leaderboard-row ${item.isYou ? "is-you" : ""}`}
										key={`${item.rank}-${item.player}`}
									>
										{item.isYou && (
											<span className="lb-pointer" aria-hidden="true" />
										)}

										<div className="leaderboard-rank">
											<RankBadge rank={item.rank} isYou={item.isYou} />
										</div>

										<div className="leaderboard-player">
											{item.isYou ? (
												<>
													<strong>YOU</strong>
													<span>{item.player}</span>
												</>
											) : (
												<span>{item.player}</span>
											)}
										</div>

										<div className="leaderboard-score">
											{Number(item.score).toLocaleString("en-US")}
										</div>
									</div>
								))}
						</div>

						<div className="leaderboard-scrollbar" aria-hidden="true">
							<button
								className="lb-scroll-arrow lb-scroll-up"
								type="button"
								tabIndex={-1}
								onClick={() => scrollByRows(-1)}
								aria-label="Scroll leaderboard up"
							/>

							<div className="lb-scroll-track">
								<span
									className="lb-scroll-thumb"
									style={{
										transform: `translateY(${scrollRatio * 220}px)`,
									}}
								/>
							</div>

							<button
								className="lb-scroll-arrow lb-scroll-down"
								type="button"
								tabIndex={-1}
								onClick={() => scrollByRows(1)}
								aria-label="Scroll leaderboard down"
							/>
						</div>
					</div>
				</div>

				<footer className="leaderboard-footer">
					<ClockIcon />

					<span>{updateText}</span>
				</footer>
			</div>
		</section>
	);
}
